import { useState, useRef } from 'react';
import { modelServicePost } from '../utils/modelService';

// TTS Style Options
const TTS_STYLES = [
    { id: 'normal', name: 'Normal', prompt: '' },
    { id: 'cheerful', name: 'Cheerful', prompt: 'Say cheerfully:' },
    { id: 'calm', name: 'Calm', prompt: 'Say in a calm, soothing voice:' },
    { id: 'excited', name: 'Excited', prompt: 'Say with excitement:' },
    { id: 'professional', name: 'Professional', prompt: 'Say in a professional tone:' },
    { id: 'whisper', name: 'Whisper', prompt: 'Say in a soft whisper:' },
    { id: 'dramatic', name: 'Dramatic', prompt: 'Say dramatically:' },
    { id: 'friendly', name: 'Friendly', prompt: 'Say in a friendly manner:' }
];

// TTS Pace Options
const TTS_PACE = [
    { id: 'slow', name: 'Slow', prompt: 'slowly' },
    { id: 'normal', name: 'Normal', prompt: '' },
    { id: 'fast', name: 'Fast', prompt: 'quickly' }
];

export const useAudioManager = () => {
    const [audioStates, setAudioStates] = useState({});
    const audioRefs = useRef({});

    // Gemini Text-to-Speech functionality
    const handleAudio = async (messageId, text, ttsSettings) => {
        if (!ttsSettings.enabled) {
            return;
        }
        
        // Get current state
        const currentState = audioStates[messageId] || { status: 'idle', audioUrl: null };
        
        // If already playing, pause
        if (currentState.status === 'playing') {
            if (audioRefs.current[messageId]) {
                audioRefs.current[messageId].pause();
                setAudioStates(prev => ({
                    ...prev,
                    [messageId]: { ...currentState, status: 'ready' }
                }));
            }
            return;
        }
        
        // If paused/ready and we have audio, play it
        if (currentState.status === 'ready' && currentState.audioUrl) {
            if (audioRefs.current[messageId]) {
                audioRefs.current[messageId].play();
                setAudioStates(prev => ({
                    ...prev,
                    [messageId]: { ...currentState, status: 'playing' }
                }));
            }
            return;
        }
        
        // If not generated yet, generate new audio with Gemini TTS
        if (currentState.status === 'idle' || !currentState.audioUrl) {
            try {
                // Update status to loading
                setAudioStates(prev => ({
                    ...prev, 
                    [messageId]: { ...currentState, status: 'loading' }
                }));

                // Build the TTS prompt with style and pace
                const stylePrompt = TTS_STYLES.find(s => s.id === ttsSettings.style)?.prompt || '';
                const pacePrompt = TTS_PACE.find(p => p.id === ttsSettings.pace)?.prompt || '';
                
                let ttsPrompt = text;
                if (stylePrompt || pacePrompt) {
                    const modifiers = [stylePrompt, pacePrompt].filter(Boolean).join(' ');
                    ttsPrompt = modifiers ? `${modifiers} ${text}` : text;
                }

                // Call the backend TTS endpoint
                const response = await modelServicePost('/tts', {
                    text: ttsPrompt,
                    voice: ttsSettings.voice,
                    style: ttsSettings.style,
                    pace: ttsSettings.pace
                });
                
                if (!response.ok) {
                    throw new Error(`Failed to generate speech: ${response.statusText}`);
                }
                
                // Simple blob approach since streaming doesn't provide speed benefit
                const audioBlob = await response.blob();
                
                if (audioBlob.size === 0) {
                    throw new Error('Received empty audio response');
                }
                
                const audioUrl = URL.createObjectURL(audioBlob);
                
                // Create and setup audio element
                if (!audioRefs.current[messageId]) {
                    audioRefs.current[messageId] = new Audio(audioUrl);
                    audioRefs.current[messageId].onended = () => {
                        setAudioStates(prev => ({
                            ...prev,
                            [messageId]: { ...prev[messageId], status: 'ready' }
                        }));
                    };
                } else {
                    audioRefs.current[messageId].src = audioUrl;
                }
                
                // Update status and store URL
                setAudioStates(prev => ({
                    ...prev,
                    [messageId]: { status: 'playing', audioUrl }
                }));
                
                // Play the audio
                await audioRefs.current[messageId].play();
            } catch (error) {
                console.error('Error generating or playing speech:', error);
                setAudioStates(prev => ({
                    ...prev,
                    [messageId]: { ...currentState, status: 'idle' }
                }));
            }
        }
    };

    // Get appropriate audio icon type based on status
    const getAudioIconType = (messageId) => {
        const state = audioStates[messageId];
        if (!state) return 'volume';
        
        switch (state.status) {
            case 'loading':
                return 'loading';
            case 'playing':
                return 'playing';
            case 'ready':
            case 'idle':
            default:
                return 'volume';
        }
    };

    // Initialize audio state for a message
    const initializeAudioState = (messageId) => {
        setAudioStates(prev => ({
            ...prev,
            [messageId]: { 
                status: 'idle',
                audioUrl: null
            }
        }));
    };

    // Cleanup audio resources
    const cleanupAudio = () => {
        Object.values(audioRefs.current).forEach(audio => {
            if (audio) {
                audio.pause();
                if (audio.src) {
                    URL.revokeObjectURL(audio.src);
                }
            }
        });
        audioRefs.current = {};
        setAudioStates({});
    };

    return {
        audioStates,
        handleAudio,
        getAudioIconType,
        initializeAudioState,
        cleanupAudio
    };
};